import { useState } from 'react';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { X, Plus } from 'lucide-react';

export default function AddActivityForm({ selectedKid, onClose }) {
  const { addActivity, family, currentUser } = useFamilyContext();
  const [formData, setFormData] = useState({
    name: '',
    category: 'other',
    energyLevel: 'medium',
    howToStart: ''
  });
  const isParent = currentUser?.isParent;

  const [selectedKids, setSelectedKids] = useState(() => {
    // For kids: always initialize with their own ID only
    // For parents: use the passed selectedKid or default to parent's own ID
    if (!isParent) {
      return [currentUser?.id].filter(Boolean);
    }
    return [selectedKid].filter(Boolean);
  });

  const categories = [
    { value: 'physical', label: 'Physical' },
    { value: 'quiet', label: 'Quiet' },
    { value: 'social', label: 'Social' },
    { value: 'creative', label: 'Creative' },
    { value: 'organization', label: 'Organization' },
    { value: 'lifeSkills', label: 'Life Skills' },
    { value: 'family', label: 'Family' },
    { value: 'project', label: 'Project' },
    { value: 'learning', label: 'Learning' },
    { value: 'academic', label: 'Academic' },
    { value: 'other', label: 'Other' }
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      alert('Please enter an activity name');
      return;
    }

    if (selectedKids.length === 0) {
      alert('Please select at least one person');
      return;
    }

    // Add activity to all selected users (kids or parent)
    selectedKids.forEach(userId => {
      const isAddingToParent = userId === currentUser?.id && currentUser?.isParent;
      addActivity(userId, {
        ...formData,
        isCustom: true,
        userType: isAddingToParent ? 'parent' : 'kid',
        availableForKids: selectedKids,
        addedBy: currentUser?.name || family.parentName
      });
    });

    // Reset form
    setFormData({
      name: '',
      category: 'other',
      energyLevel: 'medium',
      howToStart: ''
    });
    // Reset selectedKids - for kids, always reset to their own ID
    if (!isParent) {
      setSelectedKids([currentUser?.id].filter(Boolean));
    } else {
      setSelectedKids([selectedKid].filter(Boolean));
    }

    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-neutral-50 rounded-xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-xl font-bold text-stone-900">Add New Activity</h2>
            <p className="text-sm text-stone-600 mt-1">
              {!isParent 
                ? 'For your personal library'
                : selectedKids.length === 1 
                  ? `For ${selectedKids[0] === currentUser?.id ? 'yourself' : family?.kids?.find(k => k.id === selectedKids[0])?.name || 'Selected User'}`
                  : `For ${selectedKids.length} selected users`
              }
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-stone-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-stone-700 mb-2">
              Activity Name
            </label>
            <input
              type="text"
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="e.g., Bike ride, Board games, Science experiment"
              autoFocus
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-2">
              {isParent ? 'Add activity for:' : 'Add to my activities'}
            </label>
            <div className="space-y-2">
              {/* For parents: show option to add to themselves */}
              {isParent && (
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={selectedKids.includes(currentUser.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedKids([...selectedKids, currentUser.id]);
                      } else {
                        setSelectedKids(selectedKids.filter(id => id !== currentUser.id));
                      }
                    }}
                    className="mr-3 rounded border-stone-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-stone-700">My Activities</span>
                </label>
              )}
              
              {/* For parents: show kids options. For kids: only their own (disabled/hidden) */}
              {isParent ? (
                family?.kids?.map(kid => (
                  <label key={kid.id} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={selectedKids.includes(kid.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedKids([...selectedKids, kid.id]);
                        } else {
                          setSelectedKids(selectedKids.filter(id => id !== kid.id));
                        }
                      }}
                      className="mr-3 rounded border-stone-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm text-stone-700">{kid.name} (Age {kid.age})</span>
                  </label>
                ))
              ) : (
                <div className="text-sm text-stone-600 italic">
                  This activity will be added to your personal library only.
                </div>
              )}
            </div>
          </div>

          <div>
            <label htmlFor="category" className="block text-sm font-medium text-stone-700 mb-2">
              Category
            </label>
            <select
              id="category"
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              {categories.map(cat => (
                <option key={cat.value} value={cat.value}>{cat.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="energyLevel" className="block text-sm font-medium text-stone-700 mb-2">
              Energy Level
            </label>
            <select
              id="energyLevel"
              value={formData.energyLevel}
              onChange={(e) => setFormData({ ...formData, energyLevel: e.target.value })}
              className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="high">High Energy</option>
              <option value="medium">Medium Energy</option>
              <option value="low">Low Energy</option>
            </select>
          </div>

          <div>
            <label htmlFor="howToStart" className="block text-sm font-medium text-stone-700 mb-2">
              Getting Started Tips (Optional)
            </label>
            <textarea
              id="howToStart"
              value={formData.howToStart}
              onChange={(e) => setFormData({ ...formData, howToStart: e.target.value })}
              className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              rows={3}
              placeholder="Any tips or instructions to help get started with this activity..."
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-primary-600 text-neutral-50 font-medium rounded-lg hover:bg-primary-700 transition-colors"
            >
              <Plus className="w-5 h-5" />
              Add Activity
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 bg-stone-200 text-stone-700 font-medium rounded-lg hover:bg-stone-300 transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}